export type DrawingScaleRow = {
    family: 'Imperial architectural' | 'Imperial engineering' | 'Metric';
    scale: string;
    ratio: string;
};

const SCALE_ROWS: ReadonlyArray<Omit<DrawingScaleRow, 'ratio'>> = [
    { family: 'Imperial architectural', scale: '1/16 in = 1 ft', },
    { family: 'Imperial architectural', scale: '1/8 in = 1 ft', },
    { family: 'Imperial architectural', scale: '3/16 in = 1 ft', },
    { family: 'Imperial architectural', scale: '1/4 in = 1 ft', },
    { family: 'Imperial architectural', scale: '3/8 in = 1 ft', },
    { family: 'Imperial architectural', scale: '1/2 in = 1 ft', },
    { family: 'Imperial engineering', scale: '1 in = 10 ft', },
    { family: 'Imperial engineering', scale: '1 in = 20 ft', },
    { family: 'Imperial engineering', scale: '1 in = 30 ft', },
    { family: 'Imperial engineering', scale: '1 in = 40 ft', },
    { family: 'Imperial engineering', scale: '1 in = 50 ft', },
    { family: 'Metric', scale: '1:20', },
    { family: 'Metric', scale: '1:50', },
    { family: 'Metric', scale: '1:100', },
    { family: 'Metric', scale: '1:200', },
    { family: 'Metric', scale: '1:500', },
];

function inches(value: string): number {
    const [whole, numerator, denominator] = value.match(/^(?:(\d+)\s+)?(?:(\d+)\/(\d+))?$/)?.slice(1) ?? [];
    return Number(whole ?? 0) + (numerator && denominator ? Number(numerator) / Number(denominator) : 0);
}

/** Return the dimensionless ratio for a scale string displayed in the reference table. */
export function ratioForScale(scale: string): string {
    const metric = scale.match(/^1\s*:\s*(\d+)$/);
    if (metric) return `1:${Number(metric[1])}`;

    const imperial = scale.match(/^(.+?)\s*in\s*=\s*(?:(\d+)\s*ft)(?:\s*(\d+)\s*in)?$/);
    if (!imperial) throw new Error(`Unsupported drawing scale: ${scale}`);
    const drawingInches = inches(imperial[1]);
    const actualInches = Number(imperial[2]) * 12 + Number(imperial[3] ?? 0);
    return `1:${Math.round(actualInches / drawingInches)}`;
}

export const DRAWING_SCALES: ReadonlyArray<DrawingScaleRow> = SCALE_ROWS.map((row) => ({
    ...row,
    ratio: ratioForScale(row.scale),
}));

export type SourceRow = { href: string; label: string; note: string };

export const SOURCES: ReadonlyArray<SourceRow> = [
    {
        href: 'https://mesa.ucop.edu/wp-content/uploads/2017/11/5.3-General-Scale-Drawings-Resource.pdf',
        label: 'University of California Office of the President, Scaled Drawings: Architectural Scales & Engineering Scales',
        note: 'scale labels and example lists.',
    },
    {
        href: 'https://public.airportal.maa.maryland.gov/PEGS/Volume_1_-_AIRPortal/Chapter_03_CAD_Standards/3_4_Drawing_Requirements.htm',
        label: 'Maryland Department of Transportation, Maryland Aviation Administration, Drawing Requirements',
        note: 'scale factors and plotted scale table.',
    },
    {
        href: 'https://files.eric.ed.gov/fulltext/ED219653.pdf',
        label: 'Mid-America Vocational Curriculum Consortium, Basic Drafting: Book One (ERIC record ED 219 653)',
        note: 'metric scale ratios and architectural plan examples.',
    },
    {
        href: 'https://help.autodesk.com/cloudhelp/2025/ENU/AutoCAD-Core/files/GUID-89604826-0B55-4994-8214-1CA93FA66985.htm',
        label: 'Autodesk, About Setting the Plot Scale',
        note: 'precise plotting, Fit to Paper, and viewport scale.',
    },
];
